/**
 * LoginForm — the reusable credential form. Used by the in-app re-authentication panel (when a
 * session has expired but the user is still on a feature surface). Keyboard-first: native form
 * submit (Enter), labelled fields with autocomplete tokens, inline + toast error surfacing, no
 * layout shift while submitting. Enumeration-safe server errors are surfaced verbatim (already FR).
 *
 * The actual login lives in the auth store (token pair + live capability fetch via the session
 * seam); this component never touches the api client directly.
 */
import { useState, type FormEvent } from "react";
import { Button, FormField, Input, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useAuthStore } from "@/auth";
import { useT } from "@/app";
import { PasswordResetDialog } from "./PasswordResetDialog";

export interface LoginFormProps {
  /** Called after a successful login (e.g. close a re-auth modal). */
  onSuccess?: () => void;
  /** Pass through for the central/multi-tenant path (local-binary install omits it). */
  laboratoireId?: string | number;
  /** Autofocus the first field (default true). */
  autoFocus?: boolean;
}

export function LoginForm({ onSuccess, laboratoireId, autoFocus = true }: LoginFormProps) {
  const { t } = useT();
  const toast = useToast();
  const login = useAuthStore((s) => s.login);
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<{ email?: string; password?: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const submitting = status === "loading";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    // Client-side required checks (the server re-validates; this is keyboard-fast feedback).
    const next: { email?: string; password?: string } = {};
    if (!email.trim()) next.email = t("auth.login.email.required");
    if (!password) next.password = t("auth.login.password.required");
    if (next.email || next.password) {
      setFieldError(next);
      return;
    }
    setFieldError(null);
    try {
      await login({
        email: email.trim(),
        password,
        ...(laboratoireId !== undefined ? { laboratoireId } : {}),
      });
      toast.success(t("auth.login.success"));
      onSuccess?.();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t("auth.login.error");
      setFormError(message);
      toast.error(message);
    }
  };

  return (
    <>
      <form
        onSubmit={onSubmit}
        noValidate
        style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}
      >
        <FormField
          label={t("auth.login.email")}
          required
          {...(fieldError?.email ? { error: fieldError.email } : {})}
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              inputMode="email"
              autoComplete="username"
              autoFocus={autoFocus}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </FormField>

        <FormField
          label={t("auth.login.password")}
          required
          {...(fieldError?.password ? { error: fieldError.password } : {})}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </FormField>

        {formError && (
          <p
            role="alert"
            className="nv-formfield__error"
            style={{ margin: 0, color: "var(--nv-danger)" }}
          >
            {formError}
          </p>
        )}

        <Button type="submit" block loading={submitting}>
          {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setResetOpen(true)}
          style={{ alignSelf: "center" }}
        >
          {t("auth.reset.open")}
        </Button>
      </form>

      <PasswordResetDialog open={resetOpen} onClose={() => setResetOpen(false)} initialEmail={email} />
    </>
  );
}
